import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import WelcomeScreen from './WelcomeScreen';
import Register from './Register';
import SignIn from './SignIn';
import SoldInv from "./SoldInv";
import SalesStats from './SalesStats';
import SalesTrans from './SalesTrans';
import UploaderApp from './UploaderApp';
import MemberUsageLog from './MemberUsageLog';
import UpdateProfile from './UpdateProfile';
import UserDashboard from './UserDashboard';
import SalesInv from "./SalesInv";
import MemberMaint2 from './MemberMaint2';
import ProtectedRoute from './ProtectedRoute';
import { AuthProvider } from '../AuthContext';
import PipelineLayout from './PipelineLayout';
import AdminDashboard from './AdminDashboard';
import ActiveMemberView from './ActiveMemberView';
import UpdateTiers from './UpdateTiers';
import UpdatePrompts from './UpdatePrompts';
import ResetPassword from './ResetPassword';
import ConfirmEmail from './ConfirmEmail';
import ActiveUsers from './ActiveUsers';
import ClientDetails from './ClientDetails';
import SignInCheck from './SignInCheck';
import QrEntry from './QrEntry';
import CompleteRegister from './CompleteRegister';
import Terms from './Terms';
import AboutUs from './AboutUs';
import Pricing from './Pricing';
import Footer from './Footer'; // Import the new Footer component
import Vision1Requeue from './Vision1Requeue';
import StripeSaleSuccess from './StripeSaleSuccess';
import { trackPageview } from '../utils/analytics';

function AppScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    const trackablePaths = new Set(['/', '/pricing', '/about', '/welcome']);
    if (trackablePaths.has(location.pathname)) {
      trackPageview();
    }
  }, [location.pathname]);

  return null;
}

function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return null;
}

function App() {
  return (
    <Router> {/* Always mount Router first */}
      <AppScrollToTop />
      <AuthProvider> {/* Pause inside: Routes wait for stable auth */}
        <div className="flex flex-col min-h-screen bg-gray-100 pb20">
          <main className="flex-1">
          <Routes> {/* Routes now directly in flex-grow main—full real estate */}
            <Route path="/" element={<WelcomeScreen key="root" />} />
            <Route path="/welcome" element={<WelcomeScreen key="welcome" />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/register" element={<Register />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/confirm-email" element={<ConfirmEmail />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/i" element={<QrEntry />} />
            <Route path="/i/:token" element={<QrEntry />} />
            <Route path="/stripe-sale-success" element={<StripeSaleSuccess />} />
            {/* OAuth Routes outside ProtectedRoute */}
            <Route path="/signin-check" element={<SignInCheck />} />
            <Route path="/complete-register" element={<CompleteRegister />} />
            {/* Pipeline: Wrapped in Layout */}
            <Route path="/estate-sales-pipeline" element={<ProtectedRoute><PipelineLayout /></ProtectedRoute>}>
              <Route index element={<UserDashboard />} />
              <Route path="uploader" element={<UploaderApp />} />
              <Route path="userdashboard" element={<UserDashboard />} />
              <Route path="inventory" element={<SalesInv />} />
              <Route path="sold-inventory" element={<SoldInv />} />
              <Route
                path="sales-stats"
                element={
                  <ProtectedRoute requireActiveMembership={true}>
                    <SalesStats />
                  </ProtectedRoute>
                }
              />
              <Route
                path="sales-transactions"
                element={
                  <ProtectedRoute requireActiveMembership={true}>
                    <SalesTrans />
                  </ProtectedRoute>
                }
              />
              <Route path="update-profile" element={<UpdateProfile />} />
              <Route path="member-maint2" element={<MemberMaint2 />} />
              <Route path="member-usage-log" element={<MemberUsageLog />} />
              <Route path="active-member-view" element={<ActiveMemberView />} />
            </Route>
            {/* Admin routes */}
            <Route path="/admin" element={<ProtectedRoute requireAdmin={true}><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/tiers" element={<ProtectedRoute requireAdmin={true}><UpdateTiers /></ProtectedRoute>} />
            <Route path="/admin/prompts" element={<ProtectedRoute requireAdmin={true}><UpdatePrompts /></ProtectedRoute>} />
            <Route path="/admin/active-users" element={<ProtectedRoute requireAdmin={true}><ActiveUsers /></ProtectedRoute>} />
            <Route path="/admin/vision-requeue" element={<ProtectedRoute requireAdmin={true}><Vision1Requeue /></ProtectedRoute>} />
            <Route
              path="/admin/client-details/:clientId"
              element={
                <ProtectedRoute requiredRole="admin">
                  <ClientDetails />
                </ProtectedRoute>
              }
            />
          </Routes>
          </main>
          <Footer /> {/* Footer at logical bottom; pushed off if content long (scroll to see) */}
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;
